/**
 * VNNIC's place list (provinces and wards of the two-level list) as the platform holds it:
 * counts, where the entries come from, the latest syncs from VNNIC's Registrar API, and a
 * "sync now" action. The server also syncs once a day.
 */

import { MapPin, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite } from '../../components/Write';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { IntegrationError, Panel } from './shared';

export function VnPlacesPanel() {
  const { t } = useTranslation('integrations');
  const status = useApi((signal) => api.getVnPlacesStatus({ signal }), []);
  const sync = useWrite((_: null, opts) => api.syncVnPlaces(opts));
  const run = async () => {
    await sync.run(null);
    status.reload();
  };
  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-2">
          <MapPin className="size-4 text-muted-foreground" aria-hidden="true" /> {t('places.title')}
        </span>
      }
      description={t('places.description')}
    >
      {status.status === 'loading' ? <Loading rows={2} /> : null}
      {status.status === 'error' ? <IntegrationError error={status.error} /> : null}
      {status.status === 'success' ? (
        <div className="space-y-3 text-sm">
          <p>
            {t('places.counts', { provinces: formatCount(status.data.provinces), wards: formatCount(status.data.wards) })}{' '}
            {status.data.wards_from_vnnic > 0 ? <Badge tone="ok">{t('places.fromVnnic')}</Badge> : <Badge tone="warn">{t('places.fromSeed')}</Badge>}
          </p>
          {status.data.wards_from_vnnic === 0 ? <Callout tone="info">{t('places.seedHint')}</Callout> : null}
          {status.data.syncs.length ? (
            <ul className="grid gap-1 text-xs text-muted-foreground">
              {status.data.syncs.slice(0, 5).map((x) => (
                <li key={x.id} className="flex flex-wrap items-center gap-2">
                  <Badge tone={x.outcome === 'SUCCEEDED' ? 'ok' : x.outcome === 'FAILED' ? 'crit' : 'pending'}>{t(`places.outcome.${x.outcome}`)}</Badge>
                  <span>{formatDateTime(x.started_at)}</span>
                  {x.outcome === 'SUCCEEDED' ? <span>{t('places.syncCounts', { provinces: x.provinces ?? 0, wards: x.wards ?? 0, retired: x.retired ?? 0 })}</span> : null}
                  {x.error_code ? <span>{t(`places.error.${x.error_code}`, { defaultValue: x.error_code })}</span> : null}
                  <span>{x.requested_by ?? t('places.automatic')}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {sync.state.phase === 'error' ? <IntegrationError error={sync.state.error} /> : null}
          <Button size="sm" variant="outline" onClick={() => void run()} disabled={sync.busy}>
            <RefreshCw className={sync.busy ? 'animate-spin' : undefined} /> {t('places.syncNow')}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}
